import React, { useEffect, useState } from "react";

import {
  FaBoxOpen,
  FaTools,
  FaCubes,
  FaUsers,
  FaImages,
  FaHandshake,
  FaYoutube,
  FaImage,
  FaEnvelope,
  FaArrowUp,
  FaChartLine,
  FaSpinner,
} from "react-icons/fa";

import AOS from "aos";
import "aos/dist/aos.css";

import "./Dashboard.css";
import { BASE_URL } from "../../Config/config";

export default function Dashboard() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* =========================================================
     INITIALIZE AOS
  ========================================================= */

  useEffect(() => {
    AOS.init({
      duration: 700,
      easing: "ease-out-cubic",
      once: true,
      offset: 80,
      delay: 0,
    });

    return () => {
      AOS.refresh();
    };
  }, []);

  /* =========================================================
     FETCH DASHBOARD COUNTS
  ========================================================= */

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError("");

      const url = `${BASE_URL}/dashboard.php?action=get_dashboard_stats`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const result = await response.json();

      if (!result.status) {
        throw new Error(
          result.message || "Failed to fetch dashboard statistics",
        );
      }

      const data = result.data?.counts || {};

      const dashboardStats = [
        {
          title: "Products",
          value: data.products ?? 0,
          type: "blue",
          icon: <FaBoxOpen />,
        },
        {
          title: "Sub Products",
          value: data.sub_products ?? 0,
          type: "blue",
          icon: <FaBoxOpen />,
        },
        {
          title: "Product Modules",
          value: data.product_modules ?? 0,
          type: "blue",
          icon: <FaBoxOpen />,
        },
        {
          title: "Tally Modules",
          value: data.modules ?? 0,
          type: "orange",
          icon: <FaCubes />,
        },
        {
          title: "Services",
          value: data.services ?? 0,
          type: "green",
          icon: <FaTools />,
        },
        {
          title: "Team Members",
          value: data.team ?? 0,
          type: "blue",
          icon: <FaUsers />,
        },
        {
          title: "Company Clients",
          value: data.company_clients ?? 0,
          type: "green",
          icon: <FaHandshake />,
        },
        {
          title: "YouTube Videos",
          value: data.youtube_videos ?? 0,
          type: "red",
          icon: <FaYoutube />,
        },
        {
          title: "Gallery Images",
          value: data.gallery ?? 0,
          type: "purple",
          icon: <FaImages />,
        },
        {
          title: "Homepage Images",
          value: data.homepage_images ?? 0,
          type: "orange",
          icon: <FaImage />,
        },
        {
          title: "Inquiries",
          value: data.inquiries ?? 0,
          type: "yellow",
          icon: <FaEnvelope />,
        },
      ];

      setStats(dashboardStats);
    } catch (err) {
      console.error("Dashboard API Error:", err);

      setError(err.message || "Unable to load dashboard statistics");

      setStats([]);
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  /* =========================================================
     REFRESH AOS AFTER STATS LOAD
  ========================================================= */

  useEffect(() => {
    if (!loading && stats.length > 0) {
      setTimeout(() => {
        AOS.refresh();
      }, 100);
    }
  }, [loading, stats]);

  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {
    return (
      <div className="dashboard-page dashboard-loading">
        <div className="dashboard-loader">
          <FaSpinner className="spinner" />
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  /* =========================================================
     PAGE
  ========================================================= */

  return (
    <div className="dashboard-page">
      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <div
        className="dashboard-header"
        data-aos="fade-down"
        data-aos-duration="800"
      >
        <div>
          <span
            className="dashboard-label"
            data-aos="fade-right"
            data-aos-delay="100"
          >
            OVERVIEW
          </span>

          <h1 data-aos="fade-right" data-aos-delay="150">
            Dashboard
          </h1>

          <p data-aos="fade-right" data-aos-delay="200">
            Monitor your Star Computers business from one place.
          </p>
        </div>

        {/* <button
          className="view-report-btn"
          onClick={fetchDashboardStats}
          data-aos="fade-left"
          data-aos-delay="200"
        >
          <FaChartLine />
          Refresh Statistics
        </button> */}
      </div>

      {/* =====================================================
          ERROR
      ===================================================== */}

      {error && (
        <div className="dashboard-error" data-aos="fade-down">
          {error}
        </div>
      )}

      {/* =====================================================
          STAT CARDS
      ===================================================== */}

      <div className="dashboard-stats">
        {stats.map((item, index) => (
          <div
            className="stat-card"
            key={index}
            data-aos="fade-up"
            data-aos-duration="700"
          >
            <div className={`stat-icon ${item.type}`}>{item.icon}</div>

            <div className="stat-content">
              <span>{item.title}</span>

              <div className="stat-number-row">
                <h2>{item.value}</h2>

                <small>
                  <FaArrowUp />
                  Live
                </small>
              </div>

              <p>Total records</p>
            </div>
          </div>
        ))}
      </div>

      {/* =====================================================
          EMPTY STATE
      ===================================================== */}

      {!loading && stats.length === 0 && !error && (
        <div className="dashboard-empty" data-aos="fade-up">
          No dashboard statistics available.
        </div>
      )}
    </div>
  );
}
